/**
 * Header component tokens (Figma Design System — AIMS OS, node 7995:4267).
 * Typography: node 113-474. Spacing: 1x = 4px via `spacingClass`.
 * Global app navigation is `Topbar` (12706:849), not this component.
 */

import { headerTypographyClass } from '../../foundation/typography.js';
import { spacingClass } from '../../foundation/spacing.js';

export const HEADER_SIZES = ['L', 'M', 'compress'];

/** Fixed height only for Compress (28px); L/M height comes from typography + gaps. */
export const headerCompressHeightClass = 'h-7 min-h-7';

export const headerRootClass = [
  'flex w-full items-center justify-between',
  spacingClass.gap4x,
].join(' ');

export const headerLeftClass = 'flex min-w-0 flex-1';

/** 2x back↔content; 1x title↔description (L/M only) */
export const headerContentGridClass = [
  'grid min-w-0 flex-1',
  spacingClass.gapX2x,
  spacingClass.gapY1x,
].join(' ');

/** Single-row grid for Compress (no description row). */
export const headerContentGridCompressClass = [
  'grid min-w-0 flex-1',
  spacingClass.gapX2x,
].join(' ');

/** 2x title↔tag */
export const headerTitleRowCellClass = [
  'flex min-w-0 items-center self-center',
  spacingClass.gap2x,
].join(' ');

export const headerTitleClass = {
  L: `truncate ${headerTypographyClass.title.L}`,
  M: `truncate ${headerTypographyClass.title.M}`,
  compress: `truncate ${headerTypographyClass.title.compress}`,
};

export const headerDescriptionClass = headerTypographyClass.description;

export const headerDescriptionCellClass = 'min-w-0 self-start';

export const headerBackButtonClass = 'shrink-0 self-center';

export const headerBackButtonSize = {
  L: 'sm',
  M: 'sm',
  compress: 'sm',
};

/** Lucide icon size (px) for icon-only tertiary back Button. */
export const headerBackButtonIconSize = {
  L: 20,
  M: 20,
  compress: 16,
};

export const headerIconSlotClass = 'inline-flex shrink-0 items-center justify-center';

export const headerActionsClass = ['flex shrink-0 items-center', spacingClass.gap2x].join(' ');

export const headerCtaSize = {
  L: 'md',
  M: 'sm',
  compress: 'sm',
};

/** Normalize Figma / Storybook size values to `L` | `M` | `compress`. */
export function normalizeHeaderSize(size) {
  if (size == null || size === '') return 'L';
  const value = String(size).trim().toLowerCase();
  if (value === 'l' || value === 'size l') return 'L';
  if (value === 'm' || value === 'size m') return 'M';
  if (value === 'compress' || value === 'xs' || value === 'size xs') return 'compress';
  return 'L';
}

/**
 * Compress: hide description only. Tag/back/icon/CTAs follow boolean props
 * (tag defaults to Badge small via renderHeaderTag).
 * Expects an already-normalized size from {@link normalizeHeaderSize}.
 */
export function resolveHeaderVisibility(normalizedSize, flags) {
  const isCompress = normalizedSize === 'compress';

  return {
    showBackButton: flags.showBackButton,
    showDescription: !isCompress && flags.showDescription,
    showTag: flags.showTag,
    showIcon: flags.showIcon,
    showPrimaryCta: flags.showPrimaryCta,
    showSecondaryCta: flags.showSecondaryCta,
  };
}

/** Grid columns: optional back + optional icon + title/description column. */
export function buildHeaderContentGridTemplateColumns(hasBack, hasIcon) {
  const columns = [];
  if (hasBack) columns.push('auto');
  if (hasIcon) columns.push('auto');
  columns.push('minmax(0, 1fr)');
  return columns.join(' ');
}

export function resolveHeaderContentColumnIndex(hasBack, hasIcon) {
  return 1 + (hasBack ? 1 : 0) + (hasIcon ? 1 : 0);
}

export function resolveHeaderIconColumnIndex(hasBack) {
  return hasBack ? 2 : 1;
}
